import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-media-gallery-widget.st.css';
import {listViewCreateDataSource, useObservableValue} from '@wix/devzai-utils-react';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetItemList
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {doppeHideableValueGetVisibleValue} from '../../../client-server-common/types/doppe-hideable-value';
import {doppeActionTypeMediaGallery, DoppeActionTypeMediaGallery} from '../doppe-action-type-media-gallery';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {evaluateFunction, ObservableValue} from '@wix/devzai-utils-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    doppeViewerActionHandleListItemClick
} from '../../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';
import {
    useAssertWidgetRenderingContext
} from '../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';
import {doppeListActionSettingsFilterHiddenListItemsFromActionView} from '../../../client-server-common';
import {
    DoppeActionTypeGalleryListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-gallery-list-view-item/doppe-action-type-gallery-list-view-item';
import {
    DoppeActionTypeMediaGalleryItemPreview
} from '../../doppe-actions-common/doppe-action-type-media-gallery-item-preview/doppe-action-type-media-gallery-item-preview';

export const DoppeActionTypeMediaGalleryWidget = React.memo(function DoppeActionTypeTestimonialsListWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeMediaGallery.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const {
        observableCurrentItemIdToRender
    } = useAssertWidgetRenderingContext();

    const actualObservableCurrentItemIdToRender = evaluateFunction(() => {
        return observableCurrentItemIdToRender ? observableCurrentItemIdToRender : new ObservableValue<string | null>(null)
    })

    const actionSettings = actionContext.action.settings;

    const {
        listDataItems,
        layout,
        itemShowDescription,
        itemShowTitle,
        mainTitle,
        mainImage,
        mainMediaViewSettings,
        mainMediaViewLayout,
        previewShowDescription,
    } = actionSettings;

    const currentItemIdToShow = useObservableValue(actualObservableCurrentItemIdToRender);

    const widgetRenderingContextData = useAssertWidgetRenderingContext();

    const {
        currentItemToShow,
    } = useMemo(
        () => {
            const itemIndex = listDataItems.findIndex(item => item.id === currentItemIdToShow);

            return {
                currentItemToShow: itemIndex >= 0 ? listDataItems[itemIndex] : null,
            }
        }, [listDataItems, currentItemIdToShow]);


    const itemsToShow = useMemo(() => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(listDataItems);
    }, [listDataItems])

    const itemsListViewDataSource = useMemo(() => {
        return listViewCreateDataSource(itemsToShow, {
            idFunction: item => item.id
        });
    }, [itemsToShow]);

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            {evaluateFunction(() => {
                if (!currentItemToShow) {
                    if (itemsListViewDataSource.length === 0) {
                        return (
                            <DoppeActionUnavailablePreview
                                reason={DoppeActionUnavailableReason.ContentUnavailable}
                            />
                        )
                    } else {
                        return (
                            <DoppeActionTypeWidgetItemList
                                className={style(classes.listContent)}
                                actionContext={actionContext}
                                layout={layout}
                                listTitle={mainTitle}
                                media={doppeHideableValueGetVisibleValue(mainImage, undefined)}
                                mediaViewSettings={mainMediaViewSettings}
                                mediaViewLayout={mainMediaViewLayout}
                                onItemClick={eventData => {

                                    doppeViewerActionHandleListItemClick(actionContext, {
                                        itemId: eventData.id,
                                        itemIndex: itemsToShow.findIndex(item => item.id === eventData.id),
                                        widgetPopupMode: doppeActionTypeMediaGallery.resolveCustomWidgetPopupMode?.(),
                                        showHeader: false,
                                    });

                                    actionContext.reportGoalAchieved({
                                        goalType: DoppeActionGoalType.Click,
                                        innerItemId: eventData.id,
                                        innerItemPosition: listDataItems.findIndex(item => item.id === eventData.id),
                                    });
                                }}
                                renderItem={(dataItem, layout, index) => {
                                    return (
                                        <DoppeActionTypeGalleryListViewItem
                                            actionContext={actionContext}
                                            innerItemId={dataItem.id}
                                            innerItemIndex={index}
                                            listItemSettings={dataItem}
                                            layout={layout}
                                            isOneItem={itemsListViewDataSource.length === 1}
                                            hideTitleWhenPossible={!itemShowTitle}
                                            hideDescriptionWhenPossible={!itemShowDescription}
                                        />
                                    )
                                }}
                                dataSourceItems={itemsListViewDataSource}
                            />
                        )
                    }
                } else {
                    return (
                        <DoppeActionTypeMediaGalleryItemPreview
                            className={style(classes.galleryPreview, {})}
                            galleryListItemsDataSource={itemsListViewDataSource}
                            currentItemId={currentItemToShow.id}
                            dismissButtonCallback={() => {
                                widgetRenderingContextData?.closeCallback?.();
                            }}
                            listItemTemplateSectionsSettings={actionSettings.listItemTemplateSectionsSettings}
                            listItemTemplateCtaButtonsSettings={actionSettings.listItemTemplateCtaButtonsSettings}
                            actionContext={actionContext}
                            reportGoalAchieved={(itemId, goalType) => {
                                actionContext.reportGoalAchieved({
                                    goalType: goalType,
                                    innerItemId: itemId,
                                    innerItemPosition: listDataItems.findIndex(item => item.id === currentItemToShow.id),
                                });
                            }}
                            previewShowDescription={previewShowDescription}
                        />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});


